import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, CircularProgress } from '@mui/material';
import { Stack } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import { PaymentCreate } from '@/shared/types/order/payment.type';

type CreatePaymentDialogProps = {
  orderId: string;
  paymentStatuses: string[];
  paymentMethods: string[];
  paymentFormData: PaymentCreate;
  paymentFormErrors: Record<string, string>;
  errors?: string[];
  loading?: boolean;
  setPaymentFormData: (value: PaymentCreate) => void;
  onValidatePaymentFormField: (fieldName: string) => void;
  onValidatePaymentForm: () => Record<string, string>;
  onClose: () => void;
  proceed: (values: PaymentCreate) => void;
};

const CreatePaymentDialog: FC<CreatePaymentDialogProps> = ({
  orderId,
  paymentStatuses,
  paymentMethods,
  paymentFormData,
  paymentFormErrors,
  errors,
  loading,
  setPaymentFormData,
  onValidatePaymentFormField,
  onValidatePaymentForm,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['payments', 'common']);

  if (errors && errors.length) {
    return (
      <ModalNotification
        size='xs'
        fullWidth
        open
        onClose={onClose}
        dialogProps={{
          title: t('payments:createPaymentError'),
          content: (
            <Stack sx={{ py: 1 }}>
              {errors.map((error, index) => (
                <Alert key={index} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ),
          actions: [
            {
              label: t('common:close'),
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      size='xs'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: t('payments:createPayment'),
        content: (
          <Stack sx={{ py: 1 }}>
            <WaveUIForm_V2
              fields={[
                {
                  field_type: FieldType.Number,
                  field_props: {
                    name: 'transaction_amount',
                    label: t('payments:transaction_amount'),
                    value: paymentFormData.transaction_amount,
                    placeholder: t('payments:transaction_amount'),
                    required: true,
                    error: !!paymentFormErrors.transaction_amount,
                    helperText: paymentFormErrors.transaction_amount,
                    onBlur: () =>
                      onValidatePaymentFormField('transaction_amount'),
                  },
                },
                {
                  field_type: FieldType.Select,
                  field_props: {
                    name: 'payment_method',
                    label: t('payments:payment_method'),
                    value: paymentFormData.payment_method,
                    options: paymentMethods.map((method) => ({
                      value: method,
                      label: method,
                    })),
                    placeholder: t('payments:payment_method'),
                    required: true,
                    error: !!paymentFormErrors.payment_method,
                    helperText: paymentFormErrors.payment_method,
                    onBlur: () => onValidatePaymentFormField('payment_method'),
                  },
                },
                {
                  field_type: FieldType.Select,
                  field_props: {
                    name: 'status',
                    label: t('payments:status'),
                    value: paymentFormData.status,
                    options: paymentStatuses.map((status) => ({
                      value: status,
                      label: status,
                    })),
                    placeholder: t('payments:status'),
                    required: true,
                    error: !!paymentFormErrors.status,
                    helperText: paymentFormErrors.status,
                    onBlur: () => onValidatePaymentFormField('status'),
                  },
                },
              ]}
              getFieldsValues={(fieldValue) => {
                const [key, value] = Object.entries(fieldValue)[0];

                if (key === 'transaction_amount') {
                  const numberValue = value as number;

                  setPaymentFormData({
                    ...paymentFormData,
                    transaction_amount: numberValue.toString(),
                  });
                } else {
                  setPaymentFormData({ ...paymentFormData, ...fieldValue });
                }
              }}
            />
          </Stack>
        ),
        actions: [
          {
            label: t('common:cancel'),
            onClick: onClose,
          },
          {
            label: t('common:create'),
            variant: 'contained',
            icon: loading ? (
              <CircularProgress
                size='small'
                sx={{ mr: 1, width: 14, height: 14, color: 'white' }}
              />
            ) : null,
            onClick: () => {
              if (loading) return;

              const errors = onValidatePaymentForm();

              if (Object.values(errors).some((error) => error)) {
                return;
              }

              proceed({
                in_order_id: orderId,
                transaction_amount: paymentFormData.transaction_amount,
                status: paymentFormData.status,
                payment_method: paymentFormData.payment_method,
              });
            },
          },
        ],
      }}
    />
  );
};

export default CreatePaymentDialog;
